import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useMainPageTitle } from "../../contexts/PageContext";
import api from "../../services/api";
import CompetitionHeader from "../../components/organization/competition-requests/CompetitionHeader";
import TeamCard from "../../components/organization/competition-requests/TeamCard";
import VSDivider from "../../components/organization/competition-requests/VSDivider";
import CompetitionDetails from "../../components/organization/competition-requests/CompetitionDetails";
import QuickInfoSidebar from "../../components/organization/competition-requests/QuickInfoSidebar";
import OrganizationFeedModal from "../../components/organization/competition-requests/OrganizationFeedModal";
import ChatModal from "../../components/organization/competition-requests/ChatModal";
import RejectModal from "../../components/organization/competition-requests/RejectModal";
const CompetitionRequestDetail = () => {
  const { updatePageTitle } = useMainPageTitle();
  const navigate = useNavigate();
  const { requestId } = useParams();

  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showOrgFeed, setShowOrgFeed] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [showChatModal, setShowChatModal] = useState(false);
  const [teamPlayers, setTeamPlayers] = useState({});
  const [orgFeeds, setOrgFeeds] = useState({});

  const fetchRequestDetails = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/competition-requests/${requestId}`);
      setRequest(response.data.data.request);
      
      // Fetch team players for both teams
      if (response.data.data.request.senderTeamId) {
        fetchTeamPlayers(response.data.data.request.senderTeamId._id);
      }
      if (response.data.data.request.receiverTeamId) {
        fetchTeamPlayers(response.data.data.request.receiverTeamId._id);
      }
    } catch (err) {
      console.error('Error fetching request details:', err);
      setError('Failed to load competition request details');
    } finally {
      setLoading(false);
    }
  }, [requestId]);

  useEffect(() => {
    updatePageTitle("Competition Request Details", "View competition request details");
    fetchRequestDetails();
  }, [updatePageTitle, requestId, fetchRequestDetails]);

  const fetchTeamPlayers = async (teamId) => {
    try {
      // Use the new public team info endpoint that works across organizations
      const response = await api.get(`/competition-requests/teams/${teamId}/public-info`);
      
      const teamData = response.data.data?.team || {};
      
      // For own teams, we get full access, for other teams we get limited info
      if (teamData.isOwnTeam) {
        // For own teams, fetch detailed player data using the original endpoint
        try {
          const detailedResponse = await api.get(`/team/show-team-by-team-id?teamId=${teamId}`);
          const teamMembers = detailedResponse.data.teamMembers || [];
          
          // Filter for players only
          const players = teamMembers.filter(member => 
            member.type === 'player' || member.userType === 'player' || member.accountType === 'player'
          );
          
          setTeamPlayers(prev => ({
            ...prev,
            [teamId]: players
          }));
        } catch (detailedErr) {
          console.log('Could not fetch detailed team data, using count only');
          // Fallback to using member counts from public endpoint
          setTeamPlayers(prev => ({
            ...prev,
            [teamId]: Array(teamData.memberCounts?.players || 0).fill({
              firstName: 'Player',
              lastName: '',
              profilePicture: null
            })
          }));
        }
      } else {
        // For other teams, store the public info but mark as limited access
        setTeamPlayers(prev => ({
          ...prev,
          [teamId]: {
            type: 'PUBLIC_INFO',
            playerCount: teamData.memberCounts?.players || 0,
            teamInfo: teamData
          }
        }));
      }
    } catch (err) {
      console.error('Error fetching team information:', err);
      
      // Set empty array for errors
      setTeamPlayers(prev => ({
        ...prev,
        [teamId]: []
      }));
    }
  };

  const fetchOrgFeed = async (orgId) => {
    try {
      // Use the correct endpoint for getting user feed posts
      const response = await api.get(`/feed/get-all-user-feed-by-user-id?userId=${orgId}`);
      const posts = response.data.data?.posts || [];
      
      setOrgFeeds(prev => ({
        ...prev,
        [orgId]: posts
      }));
    } catch (err) {
      console.error('Error fetching organization feed:', err);
      // Set empty array for this org ID to prevent infinite loading
      setOrgFeeds(prev => ({
        ...prev,
        [orgId]: []
      }));
    }
  };



  const handleViewOrgFeed = (org) => {
    setSelectedOrg(org);
    if (!orgFeeds[org._id]) {
      fetchOrgFeed(org._id);
    }
    setShowOrgFeed(true);
  };

  const handleStartChat = (org) => {
    setSelectedOrg(org);
    setShowChatModal(true);
  };

  const handleAcceptRequest = async () => {
    if (!request.canAccept) return;

    try {
      setActionLoading(true);
      await api.post('/competition-requests/accept', { requestId });
      
      // Refresh request details
      await fetchRequestDetails();
      
      // Show success message
      console.log('Request accepted successfully');
    } catch (err) {
      console.error('Error accepting request:', err);
      setError('Failed to accept the competition request');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRejectRequest = async (reason) => {
    if (!request.canReject) return;

    try {
      setActionLoading(true);
      await api.post('/competition-requests/reject', { 
        requestId, 
        reason: reason 
      });
      
      // Refresh request details
      await fetchRequestDetails();
      
      // Close modal
      setShowRejectModal(false);
      
      // Show success message
      console.log('Request rejected successfully');
    } catch (err) {
      console.error('Error rejecting request:', err);
      setError('Failed to reject the competition request');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancelRequest = async () => {
    if (!request.canCancel) return;

    const confirmed = window.confirm('Are you sure you want to cancel this competition request?');
    if (!confirmed) return;

    try {
      setActionLoading(true);
      await api.delete(`/competition-requests/${requestId}`, {
        data: {} // Send empty body to ensure req.body is not undefined
      });
      
      // Navigate back to requests list
      navigate('/organization/competition-requests', {
        state: { 
          message: 'Competition request cancelled successfully',
          type: 'success'
        }
      });
    } catch (err) {
      console.error('Error cancelling request:', err);
      setError('Failed to cancel the competition request');
    } finally {
      setActionLoading(false);
    }
  };



  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-6"></div>
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <div className="h-6 bg-gray-200 rounded w-1/2 mb-4"></div>
            <div className="space-y-3">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="h-4 bg-gray-200 rounded w-full"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !request) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <h3 className="text-lg font-medium text-red-800 mb-2">Error</h3>
          <p className="text-red-600">{error || 'Competition request not found'}</p>
          <button
            onClick={() => navigate('/organization/competition-requests')}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
          >
            Back to Competition Requests
          </button>
        </div>
      </div>
    );
  }

  const isSentRequest = request.userRole === 'sender';

  return (
    <div className="max-w-7xl mx-auto px-4 py-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <CompetitionHeader request={request} />

      {/* Error Message */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-8">
          {/* Competition Details */}
          <CompetitionDetails request={request} />

          {/* Teams Information */}
          <div className="bg-gradient-to-br from-gray-50 to-white rounded-3xl shadow-xl border border-gray-200 p-8">
            <div className="text-center mb-10">
              <h2 className="text-4xl font-bold text-gray-900 mb-3">Teams Matchup</h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Competition between two teams to determine the better side in this exciting matchup
              </p>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
              {/* Sender Team */}
              <div className="lg:col-span-5">
                <TeamCard
                  team={request.senderTeamId}
                  teamPlayers={teamPlayers[request.senderTeamId?._id]}
                  isOwnTeam={isSentRequest}
                  isSentRequest={isSentRequest}
                  orgId={request.senderOrganizationId}
                  onViewOrgFeed={handleViewOrgFeed}
                  onStartChat={handleStartChat}
                  cardColor="from-[#E96A37] to-orange-500"
                />
              </div>

              {/* VS Divider */}
              <div className="lg:col-span-2 flex items-center justify-center">
                <VSDivider />
              </div>

              {/* Receiver Team */}
              <div className="lg:col-span-5">
                <TeamCard
                  team={request.receiverTeamId}
                  teamPlayers={teamPlayers[request.receiverTeamId?._id]}
                  isOwnTeam={!isSentRequest}
                  isSentRequest={isSentRequest}
                  orgId={request.receiverOrganizationId}
                  onViewOrgFeed={handleViewOrgFeed}
                  onStartChat={handleStartChat}
                  cardColor="from-blue-500 to-blue-600"
                />
              </div>
            </div>
          </div>

        </div>

        {/* Sidebar */}
        <div>
          <QuickInfoSidebar
            request={request}
            isSentRequest={isSentRequest}
            onAcceptRequest={handleAcceptRequest}
            onRejectRequest={() => setShowRejectModal(true)}
            onCancelRequest={handleCancelRequest}
            actionLoading={actionLoading}
          />
        </div>
      </div>

      {/* All Modals */}
      <RejectModal
        isOpen={showRejectModal}
        onClose={() => setShowRejectModal(false)}
        onReject={handleRejectRequest}
        isLoading={actionLoading}
      />

      <OrganizationFeedModal
        isOpen={showOrgFeed}
        onClose={() => setShowOrgFeed(false)}
        selectedOrg={selectedOrg}
        orgFeeds={orgFeeds}
      />

      <ChatModal
        isOpen={showChatModal}
        onClose={() => setShowChatModal(false)}
        selectedOrg={selectedOrg}
      />
    </div>
  );
};

export default CompetitionRequestDetail; 